import { AnimationProvider, Button, Chip, Icon, Text } from '@/components';
import APP_CONFIG from '@/config/app';
import { IconsName, IconVariants, TextVariants } from '@/interfaces';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CSSProperties,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const {
  MAIN_PADDING,
  MAIN_GAP,
  WHOLESTEP_DECREMENT,
  MAIN_ROUNDEDNESS,
  ALTERNATIVE_BACKDROP_BLUR,
  TEXT: { SMALL_HEADING, SMALL_HEADING_COLOR, BODY, BODY_COLOR },
} = APP_CONFIG.STYLES;
const { ANIMATION_SETTINGS } = APP_CONFIG;
const spacingConfig = {
  padding: `p${MAIN_PADDING}`,
  gap: `gap${MAIN_GAP}`,
  gapBetweenTitleAndDescription: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  gapBetweenBodyElements: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
};
const roundednessConfig = {
  roundedness: `rounded${MAIN_ROUNDEDNESS}`,
};
const sizingConfig = {
  width: 'w-full',
};
const textConfig = {
  titleSize: SMALL_HEADING,
  descriptionSize: BODY,
  titleColor: SMALL_HEADING_COLOR,
  descriptionColor: BODY_COLOR,
};
const colorConfig = {
  background: {
    primary: 'bg-primary/10',
    secondary: 'bg-secondary/15',
    accent: 'bg-accent/10',
    ai: `bg-ai/10`,
    success: 'bg-success/10',
    error: 'bg-error/10',
    disabled: 'bg-disabled/20',
    warning: 'bg-warning/10',
    transparent: 'bg-transparent',
    loading: 'bg-primary/10',
  },
  border: {
    primary: 'border border-primary/10',
    secondary: 'border border-secondary/15',
    accent: 'border border-accent/10',
    ai: `border border-ai/10`,
    success: 'border border-success/10',
    error: 'border border-error/10',
    disabled: 'border border-disabled/20',
    warning: 'border border-warning/10',
    transparent: 'border border-transparent',
    loading: 'border border-primary/10',
  },
};
export interface AlertProps {
  intent?: IconVariants['intent'];
  title?: string;
  titleSize?: TextVariants['size'];
  titleColor?: IconVariants['intent'];
  iconNode?: ReactNode;
  icon?: IconsName | ReactNode;
  showIcon?: boolean;
  description?: string;
  descriptionSize?: TextVariants['size'];
  descriptionColor?: IconVariants['intent'];
  extraContent?: ReactNode | ReactNode[];
  className?: string;
  shouldMiddleAlignIconAndText?: boolean;
  toast?: boolean;
  toastInitialDelayInMs?: number;
  toastOpenDurationInMs?: number;
  toastAutoClose?: boolean;
  toastPosition?: 'top-left' | 'top-right';
  toastIsManuallyClosable?: boolean;
  toastDistanceFromRightAndLeftEdgesInRem?: number;
  toastDistanceFromTopAndBottomEdgesInRem?: number;
  toastWidthInPx?: number;
  onClose?: () => void;
  showChip?: boolean;
  chipText?: string;
  chipIntent?: IconVariants['intent'];
}
const remToPx = (rem: number) => rem * 16;
const Alert: FC<AlertProps> = ({
  intent = 'primary',
  title = 'Information',
  titleSize = textConfig.titleSize,
  titleColor = textConfig.titleColor,
  iconNode,
  icon = 'information',
  showIcon = true,
  description = 'This is just an informational message, but we are so glad you are here!',
  descriptionSize = textConfig.descriptionSize,
  descriptionColor = textConfig.descriptionColor,
  extraContent,
  className = '',
  shouldMiddleAlignIconAndText = false,
  toast = false,
  toastInitialDelayInMs = 500,
  toastOpenDurationInMs = 10000,
  toastIsManuallyClosable = true,
  toastAutoClose = true,
  toastWidthInPx = 500,
  toastPosition = 'top-right',
  toastDistanceFromRightAndLeftEdgesInRem = 1.35,
  toastDistanceFromTopAndBottomEdgesInRem = 1.35,
  onClose,
  showChip = false,
  chipText,
  chipIntent = 'primary',
}) => {
  const [visible, setVisible] = useState(false);
  const [containerStyle, setContainerStyle] = useState<CSSProperties>({
    position: 'absolute',
    zIndex: 9999,
  });
  const validIntent = useMemo(() => intent || 'primary', [intent]);
  const marginX = useMemo(
    () => remToPx(Math.max(0, toastDistanceFromRightAndLeftEdgesInRem)),
    [toastDistanceFromRightAndLeftEdgesInRem],
  );
  const marginY = useMemo(
    () => remToPx(Math.max(0, toastDistanceFromTopAndBottomEdgesInRem)),
    [toastDistanceFromTopAndBottomEdgesInRem],
  );
  const updateContainerStyle = useCallback(() => {
    const newStyle: CSSProperties = { position: 'absolute', zIndex: 9999 };
    switch (toastPosition) {
      case 'top-left':
        newStyle.top = Math.max(window.scrollY + marginY, 0);
        newStyle.left = Math.max(window.scrollX + marginX, 0);
        break;
      case 'top-right':
        newStyle.top = Math.max(window.scrollY + marginY, 0);
        newStyle.right = Math.max(window.scrollX + marginX, 0);
        break;
    }
    setContainerStyle(newStyle);
  }, [toastPosition, marginX, marginY]);
  useEffect(() => {
    if (toast) {
      const delayTimer = setTimeout(
        () => setVisible(true),
        Math.max(0, toastInitialDelayInMs),
      );
      return () => clearTimeout(delayTimer);
    }
  }, [toast, toastInitialDelayInMs]);
  useEffect(() => {
    if (!toast) return;
    updateContainerStyle();
    window.addEventListener('scroll', updateContainerStyle, { passive: true });
    window.addEventListener('resize', updateContainerStyle);
    return () => {
      window.removeEventListener('scroll', updateContainerStyle);
      window.removeEventListener('resize', updateContainerStyle);
    };
  }, [toast, updateContainerStyle]);
  useEffect(() => {
    if (toast && toastAutoClose && toastOpenDurationInMs > 0) {
      const timer = setTimeout(() => {
        setVisible(false);
        onClose?.();
      }, Math.max(0, toastOpenDurationInMs));
      return () => clearTimeout(timer);
    }
  }, [toast, toastAutoClose, toastOpenDurationInMs, onClose]);
  const handleClose = useCallback(() => {
    setVisible(false);
    onClose?.();
  }, [onClose]);
  const nonToastClasses = useMemo(
    () =>
      `flex flex-row ${
        shouldMiddleAlignIconAndText ? 'items-center' : 'items-stretch'
      } ${sizingConfig.width} ${spacingConfig.padding} ${spacingConfig.gap} ${
        colorConfig.background[validIntent]
      } ${colorConfig.border[validIntent]} ${
        roundednessConfig.roundedness
      } ${className} ${borderClassToVisualizeElementsAndSpacing}`,
    [shouldMiddleAlignIconAndText, validIntent, className],
  );
  const toastClasses = useMemo(
    () =>
      `flex flex-row items-start overflow-hidden ${spacingConfig.padding} ${spacingConfig.gap} ${colorConfig.background[validIntent]} ${colorConfig.border[validIntent]} ${roundednessConfig.roundedness} ${className} ${borderClassToVisualizeElementsAndSpacing}`,
    [validIntent, className],
  );
  const renderContent = useCallback(
    (isToast: boolean) => (
      <>
        {showIcon && (
          <div
            className={`flex-shrink-0 ${
              !isToast && borderClassToVisualizeElementsAndSpacing
            }`}
          >
            {iconNode ||
              (typeof icon === 'string' ? (
                <Icon name={icon as IconsName} intent={intent} />
              ) : (
                icon
              ))}{' '}
            {}
          </div>
        )}
        <div
          className={`flex flex-col flex-grow ${
            !isToast ? spacingConfig.gapBetweenTitleAndDescription : ''
          } ${isToast ? 'min-h-0 overflow-auto' : ''} ${
            !isToast && borderClassToVisualizeElementsAndSpacing
          } `}
        >
          {title && (
            <div className="flex flex-row items-center">
              <Text value={title} size={titleSize} color={titleColor} />
              {showChip && chipText && (
                <Chip
                  text={chipText}
                  intent={
                    chipIntent
                      ? chipIntent === 'ai'
                        ? 'ai'
                        : chipIntent === 'error'
                        ? 'error'
                        : chipIntent === 'warning'
                        ? 'warning'
                        : chipIntent === `accent`
                        ? 'accent'
                        : 'default'
                      : intent === 'error'
                      ? 'error'
                      : intent === 'warning'
                      ? 'warning'
                      : intent === `accent`
                      ? 'accent'
                      : 'default'
                  }
                />
              )}
            </div>
          )}
          {description && (
            <Text
              value={description}
              size={descriptionSize}
              color={descriptionColor}
            />
          )}
          {!isToast && Array.isArray(extraContent) ? (
            <div
              className={`flex flex-col ${spacingConfig.gapBetweenBodyElements}`}
            >
              {extraContent.map((element, index) => (
                <div key={index}>{element}</div>
              ))}
            </div>
          ) : (
            extraContent
          )}
        </div>
      </>
    ),
    [
      showIcon,
      iconNode,
      icon,
      intent,
      title,
      titleSize,
      titleColor,
      description,
      descriptionSize,
      descriptionColor,
      extraContent,
      showChip,
      chipText,
    ],
  );
  if (!toast) {
    return <div className={nonToastClasses}>{renderContent(false)}</div>;
  }
  return createPortal(
    <AnimationProvider>
      <AnimatePresence>
        {visible && (
          <motion.div
            key="toast-alert"
            initial="initial"
            animate="open"
            exit="closed"
            variants={
              toastPosition === 'top-right'
                ? ANIMATION_SETTINGS.SLIDE_FROM.RIGHT
                : ANIMATION_SETTINGS.SLIDE_FROM.LEFT
            }
            transition={ANIMATION_SETTINGS.COMMON}
            style={containerStyle}
            className={`background backdrop-blur${ALTERNATIVE_BACKDROP_BLUR} ${borderClassToVisualizeElementsAndSpacing}`}
            role="alert"
            aria-live="polite"
          >
            <div
              className={toastClasses}
              style={{ width: `${toastWidthInPx}px` }}
            >
              {renderContent(true)}
              {toastIsManuallyClosable && (
                <Button
                  onClick={handleClose}
                  className={borderClassToVisualizeElementsAndSpacing}
                  intent="ghost"
                  icon="close"
                  iconStyles={{ intent: 'error' }}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimationProvider>,
    document.body,
  );
};
export default Alert;
